import { BigNumber } from "@yjsgames/bignumber";

/**
 * 배수 구매 순수 계산 유틸 — 도메인 무관 (Phaser/스탯/통화 종류 비의존, 단위 테스트 대상).
 *
 * 레벨마다 비용이 변하는(보통 지수/선형 누진) 시스템에서 "x10/x100/xMax 한 번에 구매"의
 * 총비용·구매 수량을 계산한다. 도메인 지식은 전부 호출자가 주입한다:
 *   - 단일 레벨 비용은 `unitCostAt(level)` 콜백으로 (statId·통화 종류 등 캡슐화),
 *   - 보유 재화는 `balance: BigNumber`로.
 * 스탯 업그레이드·환생·다이아 스탯 등이 각자 어댑터(콜백)만 끼워 공유한다.
 * phaser-brick은 maxLevel이 작아(≤200) 이진탐색 없이 단순 루프 누적으로 충분·명확하다.
 *
 * 책임 경계 — 본 모듈은 **수량·총비용 계산만** 한다 (재화 차감도, 레벨 반영도 안 함).
 * 재사용 흐름: 호출자가 `resolvePurchase`로 {count, totalCost}를 받아 → 본인이 재화 차감 →
 * 도메인 적용 메서드(예: StatUpgradeController.upgradeMany(statId, count))로 레벨을 실제 반영한다.
 * 페어: StatUpgradeController.unitCostAt(=UnitCostFn 어댑터) / upgradeMany(=적용).
 */

/** 배수 선택지. 숫자는 고정 레벨 수, "max"는 보유 재화 최대 구매. */
export type Multiplier = 1 | 10 | 100 | "max";

/** 배수 선택 UI 표시 순서 (x1 / x10 / x100 / xMax). */
export const MULTIPLIERS: readonly Multiplier[] = [1, 10, 100, "max"];

/** 특정 레벨에서 "다음 1레벨" 강화 비용을 반환하는 순수 함수 (도메인 어댑터). */
export type UnitCostFn = (level: number) => BigNumber;

/**
 * `currentLevel`부터 `count`개 레벨의 누적 비용. `maxLevel`을 넘는 분은 클램프(잔여 레벨까지만).
 * count<=0 이거나 이미 만렙이면 ZERO.
 */
export function totalCostForCount(
  unitCostAt: UnitCostFn,
  currentLevel: number,
  count: number,
  maxLevel: number,
): BigNumber {
  const n = Math.min(Math.max(0, count), Math.max(0, maxLevel - currentLevel));
  let total = BigNumber.ZERO;
  for (let i = 0; i < n; i++) total = total.add(unitCostAt(currentLevel + i));
  return total;
}

/**
 * `balance`로 살 수 있는 최대 레벨 수 (`maxLevel` 클램프). 한 레벨씩 누적 비용을 더하며
 * 보유 재화로 감당 가능한 동안 카운트한다. 1레벨도 못 사면 0.
 */
export function maxAffordableCount(
  unitCostAt: UnitCostFn,
  currentLevel: number,
  balance: BigNumber,
  maxLevel: number,
): number {
  const remaining = Math.max(0, maxLevel - currentLevel);
  let count = 0;
  let total = BigNumber.ZERO;
  while (count < remaining) {
    const next = total.add(unitCostAt(currentLevel + count));
    // 비용 비증가(0/음수 비정상 입력) 시 무한 진행 방지 — maxLevel이 무한대인 도메인 재사용 대비.
    if (next.lte(total)) break;
    if (balance.lt(next)) break;
    total = next;
    count++;
  }
  return count;
}

/**
 * 선택 배수 → 실제 구매 레벨 수 + 총비용 (maxLevel 클램프 포함).
 * - 고정 배수(1/10/100): 잔여 레벨로 클램프한 의도 수량 + 그 총비용. **재화 부족은 반영하지 않는다**
 *   (호출자가 `balance.gte(totalCost)`로 affordable 판정 → 부족 시 버튼 비활성).
 * - "max": 보유 재화로 살 수 있는 최대 수량 + 총비용 (0 가능).
 */
export function resolvePurchase(
  unitCostAt: UnitCostFn,
  currentLevel: number,
  maxLevel: number,
  balance: BigNumber,
  mult: Multiplier,
): { count: number; totalCost: BigNumber } {
  const count =
    mult === "max"
      ? maxAffordableCount(unitCostAt, currentLevel, balance, maxLevel)
      : Math.min(mult, Math.max(0, maxLevel - currentLevel));
  return { count, totalCost: totalCostForCount(unitCostAt, currentLevel, count, maxLevel) };
}
